
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../utils/api';

// Removed TypeScript interfaces

const initialState = {
  messages: [],
  contacts: [],
  conversations: [],
  conversationMessages: [],
  incomingRequests: [],
  outgoingRequests: [],
  loading: false,
  error: null,
  contactsLoading: false,
  contactsError: null,
};

// Async thunk to fetch messages for the authenticated user
export const fetchMessages = createAsyncThunk(
  'messages/fetchMessages',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/messages');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk to send a new message
export const sendMessage = createAsyncThunk(
  'messages/sendMessage',
  async (
    { toUserId, content, attachment },
    { rejectWithValue }
  ) => {
    try {
      let payload;
      let config;
      if (attachment) {
        const formData = new FormData();
        formData.append('toUserId', String(toUserId));
        formData.append('content', content || '');
        formData.append('attachment', attachment);
        payload = formData;
        config = { headers: { 'Content-Type': 'multipart/form-data' } };
      } else {
        payload = { toUserId, content };
      }

      const response = await api.post('/messages', payload, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

// Async thunk to fetch contacts (citizens and volunteers)
export const fetchContacts = createAsyncThunk(
  'messages/fetchContacts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/messages/contacts');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchConversations = createAsyncThunk(
  'messages/fetchConversations',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/messages/conversations');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchConversationMessages = createAsyncThunk(
  'messages/fetchConversationMessages',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await api.get(`/messages/conversations/${contactId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const markConversationSeen = createAsyncThunk(
  'messages/markConversationSeen',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/messages/conversations/${contactId}/seen`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchIncomingRequests = createAsyncThunk(
  'messages/fetchIncomingRequests',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/messages/requests/incoming');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchOutgoingRequests = createAsyncThunk(
  'messages/fetchOutgoingRequests',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/messages/requests/outgoing');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendChatRequest = createAsyncThunk(
  'messages/sendChatRequest',
  async (toUserId, { rejectWithValue }) => {
    try {
      const response = await api.post('/messages/requests', { toUserId });
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const acceptChatRequest = createAsyncThunk(
  'messages/acceptChatRequest',
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/messages/requests/${requestId}/accept`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const rejectChatRequest = createAsyncThunk(
  'messages/rejectChatRequest',
  async (requestId, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/messages/requests/${requestId}/reject`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const blockContact = createAsyncThunk(
  'messages/blockContact',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/messages/contacts/${contactId}/block`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const unblockContact = createAsyncThunk(
  'messages/unblockContact',
  async (contactId, { rejectWithValue }) => {
    try {
      const response = await api.patch(`/messages/contacts/${contactId}/unblock`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const messageSlice = createSlice({
  name: 'messages',
  initialState,
  reducers: {
    clearMessages(state) {
      state.messages = [];
      state.error = null;
      state.loading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMessages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.messages = action.payload.data || action.payload;
      })
      .addCase(fetchMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(sendMessage.pending, (state) => {
        state.error = null;
      })
      .addCase(sendMessage.fulfilled, (state, action) => {
        state.messages.unshift(action.payload.data || action.payload); // Add new message at the start (assuming order by createdAt desc)
      })
      .addCase(sendMessage.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchContacts.pending, (state) => {
        state.contactsLoading = true;
        state.contactsError = null;
      })
      .addCase(fetchContacts.fulfilled, (state, action) => {
        state.contactsLoading = false;
        state.contacts = action.payload.data || action.payload;
      })
      .addCase(fetchContacts.rejected, (state, action) => {
        state.contactsLoading = false;
        state.contactsError = action.payload;
      })
      .addCase(fetchConversations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchConversations.fulfilled, (state, action) => {
        state.loading = false;
        state.conversations = action.payload.data || action.payload;
      })
      .addCase(fetchConversations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchConversationMessages.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchConversationMessages.fulfilled, (state, action) => {
        state.loading = false;
        state.conversationMessages = action.payload.data || action.payload;
      })
      .addCase(fetchConversationMessages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(markConversationSeen.fulfilled, (state) => {
        // no-op here; conversation refresh handled in UI
      })
      .addCase(fetchIncomingRequests.fulfilled, (state, action) => {
        state.incomingRequests = action.payload.data || action.payload;
      })
      .addCase(fetchOutgoingRequests.fulfilled, (state, action) => {
        state.outgoingRequests = action.payload.data || action.payload;
      })
      .addCase(sendChatRequest.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(acceptChatRequest.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(rejectChatRequest.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(blockContact.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(unblockContact.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { clearMessages } = messageSlice.actions;

export default messageSlice.reducer;
